import { Component } from '@theme/component';
import { morph } from '@theme/morph';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} content – The container for recently viewed content.
 * @property {HTMLElement} currentIndex – The current slide index indicator.
 * @property {HTMLButtonElement} prevBtn – The previous button.
 * @property {HTMLButtonElement} nextBtn – The next button.
 * @property {HTMLElement[]} slides – The slide elements.
 */

/**
 * A custom element that loads and displays recently viewed products.
 *
 * @extends Component<Refs>
 */
class RecentlyViewedProducts extends Component {
  /** @type {number} */
  currentSlideIndex = 1;
  /** @type {number} */
  totalSlides = 0;

  async connectedCallback() {
    super.connectedCallback();
    await this.#loadProducts();
    this.#reInitSlider();
    this.#setupPaginationListeners();
  }

  /**
   * Loads recently viewed products via the search API.
   */
  async #loadProducts() {
    const searchQuery = this.#searchQueryString;
    if (searchQuery === '') return;

    try {
      const response = await fetch(
        `${Theme.routes.search_url}?type=product&q=${searchQuery}&section_id=${this.sectionId}`
      );
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newElement = doc.querySelector('recently-viewed-products');

      if (!newElement || !newElement.hasChildNodes()) return;

      const newContent = newElement.querySelector('[ref="content"]') || newElement;

      if (this.refs.content) {
        morph(this.refs.content, newContent);
        this.#reInitSlider();
      } else {
        this.innerHTML = newElement.innerHTML;
        this.#reInitSlider();
      }
    } catch (error) {
      console.error('Error loading recently viewed products:', error);
    }
  }

  /**
   * Builds a search query string from localStorage items.
   */
  get #searchQueryString() {
    const items = JSON.parse(localStorage.getItem('theme:recently-viewed-products') || '[]');
    const excludeId = this.getAttribute('exclude-product-id');

    if (excludeId && items.includes(parseInt(excludeId))) {
      items.splice(items.indexOf(parseInt(excludeId)), 1);
    }

    return items
      .map((/** @type {number} */ item) => 'id:' + item)
      .slice(0, this.productsCount)
      .join(' OR ');
  }

  get sectionId() {
    return this.getAttribute('section-id');
  }

  get productsCount() {
    return parseInt(this.getAttribute('products-count') || '4');
  }

  /**
   * Reinitialises the slider after a morph or load.
   */
  #reInitSlider() {
    const slider = /** @type {any} */ (this.querySelector('slider-component'));
    if (slider && typeof slider.reInit === 'function') {
      slider.reInit();
    }
    this.#initPagination();
  }

  /**
   * Initialises the pagination state.
   */
  #initPagination() {
    const slides = this.querySelectorAll('.recently-viewed-products__slide');
    this.totalSlides = slides.length;
    this.currentSlideIndex = 1;
    this.#updatePaginationIndicator();
  }

  /**
   * Sets up click listeners for pagination buttons.
   */
  #setupPaginationListeners() {
    if (this.refs.prevBtn) {
      this.refs.prevBtn.addEventListener('click', () => this.#prevSlide());
    }
    if (this.refs.nextBtn) {
      this.refs.nextBtn.addEventListener('click', () => this.#nextSlide());
    }

    const slider = /** @type {any} */ (this.querySelector('slider-component'));
    if (!slider) return;

    slider.addEventListener('slideChange', (/** @type {CustomEvent} */ event) => {
      if (event.detail && typeof event.detail.index === 'number') {
        this.currentSlideIndex = event.detail.index + 1;
        this.#updatePaginationIndicator();
      }
    });
  }

  /**
   * Navigates to the previous slide.
   */
  #prevSlide() {
    const slider = /** @type {any} */ (this.querySelector('slider-component'));
    if (slider && typeof slider.scrollPrev === 'function') {
      slider.scrollPrev();
    }

    let newIndex = this.currentSlideIndex - 1;
    if (newIndex < 1) newIndex = this.totalSlides;
    this.currentSlideIndex = newIndex;
    this.#updatePaginationIndicator();
    this.#scrollToSlide(newIndex - 1);
  }

  /**
   * Navigates to the next slide.
   */
  #nextSlide() {
    const slider = /** @type {any} */ (this.querySelector('slider-component'));
    if (slider && typeof slider.scrollNext === 'function') {
      slider.scrollNext();
    }

    let newIndex = this.currentSlideIndex + 1;
    if (newIndex > this.totalSlides) newIndex = 1;
    this.currentSlideIndex = newIndex;
    this.#updatePaginationIndicator();
    this.#scrollToSlide(newIndex - 1);
  }

  /**
   * Scrolls to a specific slide.
   * @param {number} index - The 0-based slide index.
   */
  #scrollToSlide(index) {
    const slides = this.querySelectorAll('.recently-viewed-products__slide');
    const slide = slides[index];
    if (slide) {
      slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  }

  /**
   * Updates the pagination indicator text.
   */
  #updatePaginationIndicator() {
    if (this.refs.currentIndex) {
      this.refs.currentIndex.textContent = String(this.currentSlideIndex);
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
